/**
 * The power heart on the monsters (02 §7.5): taking it (D_34A0) turns every monster in the C64's
 * states 1..$0A - dropping in, waiting, walking, flying, escaping; not trapped or dead - into state
 * $16: light blue, frozen, falling to the floor (D_2785), then lying there ($17, L_27CB). A monster let
 * out of its bubble meanwhile comes out in state $16 too (D_34A0 leaves $16 in D_5ABF, which the
 * escape at L13D2 uses). Touching a frozen monster kills it (grapes, 3,000); it no longer kills. The
 * clock does not stop them ($16 and $17 are not in 1..$0A), and a shot may still trap one (L105B takes
 * states >= $16) - out of that bubble it freezes again.
 */

import { hit, hitRow, OFF, probe } from './map';
import { fallStep } from './motion';
import { killMonsterBy } from '../bubbles';
import type { Monster, World } from '../types';

const FALLING = 1;
const LANDED = 2;

/** D_34A0 for one monster, or a monster let out of its bubble under the heart. */
export function freezeByHeart(m: Monster): void {
  const a = m.ai;
  m.frozen = true;
  m.state = 'active';
  a.heart = FALLING;
  a.delay = 0;
  a.escaping = false;
  a.reserved = false;
}

/** While the heart is on, catch any monster that is out and not yet frozen. */
export function applyHeart(world: World): void {
  if (world.power.heart < 0) return;
  for (const m of world.monsters) {
    if (!m.frozen && m.state !== 'trapped' && m.state !== 'dead') freezeByHeart(m);
  }
}

/**
 * L_27CB: any player in the game - in any state but absent, as the C64 tests only for 0 - within
 * 16 px kills it; the credit is that player's (D_1A6F with Y = the player, player 2 first).
 */
function touched(world: World, m: Monster): boolean {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play' && p.state !== 'dying') continue;
    if (Math.abs(m.x - p.x) < 16 && Math.abs(m.y - p.y) < 16) {
      killMonsterBy(world, m.id, 'heart', id);
      return true;
    }
  }
  return false;
}

/**
 * D_2785 (falling) / L_27CB (landed): one update of a frozen monster. Falling, it passes down through
 * any platform its lower half is in (cell pointer + $29..$2B); with the floor under its feet (+$51 or
 * +$52 solid) it has landed; otherwise it falls 2 px with X made even and Y odd, landing on an 8-px
 * line as a walker does (D_272F into D_273D). No animation. Then the players' touch.
 */
export function heartTick(world: World, m: Monster): void {
  const a = m.ai;
  if (a.heart === FALLING) {
    const p = probe(m);
    if (!hitRow(world, p, 0x29) && (hit(world, p, OFF.floor) || hit(world, p, OFF.floor + 1))) {
      a.heart = LANDED;
    } else {
      m.x &= 0xfe;
      m.y |= 1;
      if (fallStep(world, m, p)) a.heart = LANDED;
    }
  }
  touched(world, m);
}
